import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Lock, StickyNote, X } from "lucide-react";
import { Avatar } from "../ui/Avatar";
import { cn } from "../../utils/cn";
import { workflowErrorMessage } from "./workflowErrorMessage";
import { createPrivateNote } from "../../services/helpdeskWorkflowService";
import useHelpdeskTeam, { displayNameOf, filterTeam, initialsOf, userIdOf } from "./useHelpdeskTeam";

// ─── Editor de nota privada ──────────────────────────────────────────────────
// Pestaña "Nota privada" del composer (estilo Chatwoot): fondo ámbar para que
// nunca se confunda con una respuesta al contacto. Guarda con
// createPrivateNote — jamás pasa por el camino de envío al contacto — y su
// borrador es estado LOCAL: teclear acá no re-renderiza el detalle ni el hilo.
//
// "@" abre la lista del equipo; elegir a alguien inserta "@Nombre " y recuerda
// su id. Al guardar solo viajan los ids cuyo "@Nombre" sigue en el texto (si el
// agente borró la mención, no se avisa a esa persona).

const MAX_NOTE = 4000;
const MAX_MENTIONS = 10;
const MAX_QUERY = 30;
const MAX_SUGGESTIONS = 6;

/**
 * Consulta de mención activa justo antes del caret: "@" al inicio o tras un
 * espacio, sin saltos de línea ni otra "@" en medio. Exportada para test.
 * @returns {{start:number, query:string}|null}
 */
export function activeMentionQuery(text, caret) {
  const before = String(text || "").slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (query.length > MAX_QUERY || /[\n@]/.test(query)) return null;
  return { start: at, query };
}

/**
 * Ids mencionados que siguen presentes en el texto, sin repetir y con tope.
 * @param {string} text
 * @param {Array<{id:string, name:string}>} picked
 */
export function mentionIdsInText(text, picked) {
  const out = [];
  for (const { id, name } of picked) {
    if (!id || out.includes(id)) continue;
    if (String(text).includes(`@${name}`)) out.push(id);
    if (out.length >= MAX_MENTIONS) break;
  }
  return out;
}

/**
 * @param {object} props
 * @param {string} props.conversationId
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.autoFocus]
 */
export default function PrivateNoteComposer({ conversationId, disabled = false, autoFocus = false }) {
  const { t } = useTranslation();
  const listId = useId();
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]); // [{id, name}]
  const [mention, setMention] = useState(null); // {start, query} | null
  const [cursor, setCursor] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);
  // El equipo se pide recién cuando el agente escribe "@" por primera vez.
  const [teamWanted, setTeamWanted] = useState(false);
  const { team, error: teamError, reload: reloadTeam } = useHelpdeskTeam({ enabled: teamWanted });

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Auto-grow simple (la nota es un textarea aparte del composer principal).
  const grow = useCallback((el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => {
    const pos = pendingCaret.current;
    if (pos == null) return;
    pendingCaret.current = null;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
    grow(el);
  }, [text, grow]);

  const suggestions = useMemo(
    () => (mention ? filterTeam(team, mention.query).slice(0, MAX_SUGGESTIONS) : []),
    [team, mention],
  );

  useEffect(() => {
    setCursor(0);
  }, [mention?.query]);

  const updateMention = (value, caret) => {
    const next = activeMentionQuery(value, caret);
    setMention(next);
    if (next && !teamWanted) setTeamWanted(true);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    setError(null);
    grow(e.target);
    updateMention(value, e.target.selectionStart ?? value.length);
  };

  const choose = (user) => {
    if (!mention || !user) return;
    const id = userIdOf(user);
    const name = displayNameOf(user);
    const el = inputRef.current;
    const caret = el?.selectionStart ?? text.length;
    const insert = `@${name} `;
    const next = text.slice(0, mention.start) + insert + text.slice(caret);
    pendingCaret.current = mention.start + insert.length;
    setText(next);
    setPicked((prev) => (prev.some((p) => p.id === id) ? prev : [...prev, { id, name }]));
    setMention(null);
  };

  const save = async () => {
    const content = text.trim();
    if (!content || saving || disabled || !conversationId) return;
    if (content.length > MAX_NOTE) {
      setError(t("La nota supera los {{n}} caracteres.").replace("{{n}}", String(MAX_NOTE)));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createPrivateNote(conversationId, {
        content,
        mentions: mentionIdsInText(content, picked),
      });
      // El mensaje llega al hilo por el SSE (new_message), igual que una respuesta.
      setText("");
      setPicked([]);
      setMention(null);
      if (inputRef.current) inputRef.current.style.height = "auto";
    } catch (err) {
      setError(workflowErrorMessage(err, t("No se pudo guardar la nota.")));
    } finally {
      setSaving(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    const listOpen = !!mention && suggestions.length > 0;
    if (listOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => (c + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => (c - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        choose(suggestions[cursor]);
        return;
      }
    }
    if (e.key === "Escape" && mention) {
      e.preventDefault();
      e.stopPropagation();
      setMention(null);
      return;
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
      e.preventDefault();
      save();
    }
  };

  const listOpen = !!mention;
  const activeOptionId =
    listOpen && suggestions[cursor] ? `${listId}-opt-${userIdOf(suggestions[cursor])}` : undefined;

  return (
    // Transparent: it lives inside the composer card, which carries the amber
    // tint/outline in note mode (second visual pass).
    <div>
      <div className="relative px-[6px] pt-[4px] pb-[6px] flex items-end gap-[4px]">
        {listOpen && (
          <div
            id={listId}
            role="listbox"
            aria-label={t("Mencionar a alguien del equipo")}
            className="absolute left-3 bottom-full mb-1 z-30 w-72 max-w-[calc(100%-1.5rem)] rounded-lg border border-border bg-bg shadow-elevated py-1"
          >
            {team === null ? (
              <div className="px-3 py-2 text-xs text-fg-muted">{t("Cargando equipo…")}</div>
            ) : teamError ? (
              <div className="px-3 py-2 flex items-center gap-2 text-xs text-status-error">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{t("No se pudo cargar el equipo.")}</span>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={reloadTeam}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {t("Reintentar")}
                </button>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-fg-muted">
                {t("Nadie del equipo coincide con «{{texto}}».").replace("{{texto}}", mention.query)}
              </div>
            ) : (
              suggestions.map((u, i) => {
                const id = userIdOf(u);
                return (
                  <button
                    key={id}
                    id={`${listId}-opt-${id}`}
                    type="button"
                    role="option"
                    aria-selected={i === cursor}
                    // mousedown y no click: el blur del textarea perdería el caret.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(u);
                    }}
                    onMouseEnter={() => setCursor(i)}
                    className={cn(
                      "w-full min-h-[40px] flex items-center gap-2 px-3 py-1.5 text-left text-sm text-fg",
                      i === cursor ? "bg-bg-muted" : "hover:bg-bg-muted",
                    )}
                  >
                    <Avatar size="sm" tone="neutral" initials={initialsOf(u)} />
                    <span className="flex-1 min-w-0 truncate">{displayNameOf(u)}</span>
                  </button>
                );
              })
            )}
          </div>
        )}

        <textarea
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => updateMention(text, e.target.selectionStart ?? text.length)}
          onBlur={() => setMention(null)}
          rows={1}
          maxLength={MAX_NOTE}
          disabled={saving || disabled}
          placeholder={t("Nota interna para el equipo; usa @ para mencionar…")}
          aria-label={t("Nota privada")}
          aria-autocomplete="list"
          aria-controls={listOpen ? listId : undefined}
          aria-activedescendant={activeOptionId}
          className="flex-1 min-h-[40px] max-h-40 px-[8px] py-[10px] rounded-md border-0 bg-transparent focus:bg-transparent text-[16px] md:text-[14px] leading-[20px] text-fg placeholder:text-fg-muted placeholder:whitespace-nowrap placeholder:overflow-hidden placeholder:text-ellipsis resize-none focus:outline-none focus:ring-0 focus:shadow-none disabled:opacity-50"
        />
        <button
          type="button"
          onClick={save}
          disabled={!text.trim() || saving || disabled}
          aria-label={t("Guardar nota privada")}
          title={t("Guardar nota (Enter)")}
          /* text-bg sobre status-warning: mismo criterio que "Cerrar caso" con
             status-success (el token se invierte en oscuro y el blanco no
             alcanzaría contraste). */
          className="inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-lg bg-status-warning text-bg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shrink-0"
        >
          {saving ? (
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
          ) : (
            <StickyNote className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {error && (
        <div
          role="alert"
          className="mx-[8px] mb-[8px] px-[10px] py-[6px] rounded-lg text-[12px] bg-status-error-soft text-status-error flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="p-0.5 rounded hover:bg-status-error/10"
            aria-label={t("Cerrar")}
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
      <p className="px-[14px] pb-[8px] -mt-[2px] flex items-center gap-1 text-[11px] text-status-warning">
        <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />
        {t("Solo la ve el equipo: no llega al contacto ni a la IA.")}
      </p>
    </div>
  );
}
